import React, { Component } from "react";
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field, FieldProps, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { Button, Grid, Header, Modal } from 'semantic-ui-react';
import { required } from 'utils/validatorUtils';
import Input from 'components/final-form/Input';
import { addProcedureGroup, availableProcedures, editProcedureGroup } from 'services/procedureGroupServices';
import { ProcedureDto, UpsertProcedureGroupDto } from 'ts-types/api.types';
import { getAllSelectedProceduresByTariffId } from 'services/procedureServices';
import ProcedureGroupSegment from 'components/procedures/segments/ProcedureGroupSegment'
import axios, { CancelTokenSource } from 'axios';
import LoaderComponent from 'components/LoaderComponent';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';

interface Props extends MessageSourceProps {
  onCancel: () => void,
  onSave: () => void,
  modalOpen: boolean,
  procedureGroupId?: number,
  procedureGroup?: UpsertProcedureGroupDto
  tariffId: number,
  tarmedTariff?: Boolean,
}

interface State {
  procedureGroup?: Partial<UpsertProcedureGroupDto>,
  formDataLoading: boolean
  selectedProcedures: Array<number>,
  procedures: Array<ProcedureDto>,
  availableProcedures: Array<number>,
  errorMessage?: string,
  cancelTokenSource: CancelTokenSource
}

const VALIDATED_FIELDS: Array<string> = [
  "description",
  "descriptionFr",
  "descriptionIt",
  "descriptionEn"
];

const initialProcedureGroup: Partial<UpsertProcedureGroupDto> = {
  description: "",
  descriptionEn: "",
  descriptionFr: "",
  descriptionIt: "",
  tariffId: -1
};

class UpsertProcedureGroupsForm extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {procedureGroup, tariffId, getMessage} = this.props;
    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      selectedProcedures: procedureGroup ? procedureGroup.procedures : [],
      procedures: [],
      availableProcedures: [],
      formDataLoading: true,
      cancelTokenSource
    };

    const methods: Array<Promise<any>> = [
      getAllSelectedProceduresByTariffId(tariffId, cancelTokenSource, "code"),
      availableProcedures(tariffId, cancelTokenSource)
    ];

    const handleError = () => {
      this.setState({
        errorMessage: getMessage("error.general"),
        cancelTokenSource
      })
    };

    const onFinally = () => {
      this.setState({
        formDataLoading: false
      })
    };

    Promise.all(methods)
        .then(responses => {
          this.setState({
            procedures: responses[0],
            availableProcedures: responses[1]
          })
        })
        .catch(handleError)
        .finally(onFinally);

    initialProcedureGroup.tariffId = tariffId;
  }

  setSelectedProcedures = (selectedProcedures: Array<number>) => {
    this.setState({
      selectedProcedures: selectedProcedures
    })
  };

  handleSubmit = (values: Partial<UpsertProcedureGroupDto>): void => {

    const {procedureGroupId, getMessage, onSave} = this.props;
    const {selectedProcedures} = this.state;

    values.procedures = selectedProcedures;

    const handleError = () => {

      this.setState({
        errorMessage: getMessage("error.general")
      })
    };

    if (procedureGroupId) {
      editProcedureGroup(procedureGroupId, values)
          .then(onSave)
          .catch(handleError)
    } else {
      addProcedureGroup(values)
          .then(onSave)
          .catch(handleError)
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {

    const {formDataLoading} = this.state;
    const {getMessage} = this.props;

    return formDataLoading
        ? <LoaderComponent
            message={getMessage("procedureGroups.formLoading")}
            dimmerProps={{active: true, inverted: false, page: true}}
            inverted/>
        : this.renderModal();
  }

  renderModal = (): JSX.Element => {

    const {modalOpen, onCancel, procedureGroup, getMessage} = this.props;

    return (
        <Modal
            open={modalOpen}
            onClose={onCancel}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("header.navigation.procedureGroups")}</Modal.Header>
          <FormModalContentContainer>
            <FinalForm
                onSubmit={(values) => this.handleSubmit(values)}
                initialValues={procedureGroup ? procedureGroup : initialProcedureGroup}
                subscription={{values: true, pristine: true}}
                render={this.renderForm}
            />
          </FormModalContentContainer>
        </Modal>
    )
  };

  renderForm = ({handleSubmit, submitting}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCancel} = this.props;
    const {selectedProcedures, availableProcedures, procedures, errorMessage} = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              {errorMessage
              && (
                  <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                    {errorMessage}
                  </StyledErrorMessage>
              )
              }
              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column>
                    {this.renderDescriptionSection()}
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row style={{marginTop: "1rem"}}>
                  <Grid.Column>
                    <ProcedureGroupSegment
                        procedures={procedures}
                        selectedProcedures={selectedProcedures && selectedProcedures.length > 0 ? selectedProcedures : []}
                        availableProcedures={availableProcedures}
                        setSelectedProcedures={this.setSelectedProcedures}
                    />
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    )
        ;
  };

  renderDescriptionSection = (): React.ReactNode => {

    const {getMessage} = this.props;

    const descriptionRows: Array<Array<string>> = [
      ["description"],
      ["descriptionFr"],
      ["descriptionIt"],
      ["descriptionEn"]
    ];

    const validator = (value: string) => required(value);
    const descriptionSection = descriptionRows.map((row, index) => {
      return (
          <Grid.Row key={index}>
            {
              row.map((field, index1) => {
                let fieldProps: FieldProps = {
                  name: field,
                  validate: VALIDATED_FIELDS.includes(field) ? validator : undefined,
                  component: Input
                };

                return (
                    <React.Fragment key={index1}>
                      <Grid.Column width={5} verticalAlign="middle">

                        {
                          field === "description" ?
                              `${getMessage(field)}:` :
                              `${getMessage(`procedureGroup.${field}`)}:`
                        }
                      </Grid.Column>
                      <Grid.Column width={11} verticalAlign="middle">
                        <Field
                            fluid
                            {...fieldProps}
                            size="mini"
                        />
                      </Grid.Column>
                    </React.Fragment>
                );
              })
            }
          </Grid.Row>
      );
    });

    return (
        <PraxFormGrid>
          <Grid.Row>
            <Grid.Column>
              <Header as='h5' style={{marginTop: "1rem"}}>
                {getMessage("description")}
              </Header>
            </Grid.Column>
          </Grid.Row>
          {descriptionSection}
        </PraxFormGrid>
    );
  };
}

export default withMessages(UpsertProcedureGroupsForm);
